import SearchIcon from '@mui/icons-material/Search';
import {
    FormControl,
    InputAdornment,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
    useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useId, useMemo, useState } from 'react';
import LawFirmName from '@/components/LawFirmName';
import { useLawFirms } from '@/queries/lawFirms';

export type LawFirmSelectValue = number | undefined;

interface Props {
    value: LawFirmSelectValue;
    allOptionLabel?: string;
    myLawFirmId?: number;
    myLawFirmName?: string;
    includeTestFirms?: boolean;
    excludeLawFirmId?: number;
    onChange(value: LawFirmSelectValue): void;
}

export default function SearchableLawFirmSelect({
    value,
    allOptionLabel = 'All',
    myLawFirmId,
    myLawFirmName,
    includeTestFirms = true,
    excludeLawFirmId,
    onChange,
}: Props) {
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState('');

    const labelId = useId();

    const { data: lawFirms = [] } = useLawFirms(includeTestFirms);

    const filteredLawFirms = useMemo(
        () =>
            lawFirms.filter((lawFirm) => {
                if (excludeLawFirmId && lawFirm.lawFirmId === excludeLawFirmId) {
                    return false;
                }
                return lawFirm.companyName.toLowerCase().includes(searchTerm.toLowerCase());
            }),
        [lawFirms, searchTerm, excludeLawFirmId],
    );

    const selectValue = value ?? myLawFirmId ?? '';

    const handleChange = (event: SelectChangeEvent<LawFirmSelectValue>) => {
        const rawValue = event.target.value;

        if (rawValue === '') {
            onChange(undefined);
            return;
        }

        const parsedNumber = typeof rawValue === 'number' ? rawValue : Number(rawValue);
        if (Number.isNaN(parsedNumber)) {
            onChange(undefined);
            return;
        }

        if (myLawFirmId !== undefined && parsedNumber === myLawFirmId) {
            onChange(undefined);
            return;
        }

        onChange(parsedNumber);
    };

    const renderValue = (selected: LawFirmSelectValue) => {
        if (typeof selected === 'number') {
            if (selected === myLawFirmId && myLawFirmName) {
                return myLawFirmName;
            }
            const lawFirm = lawFirms.find((firm) => firm.lawFirmId === selected);
            return lawFirm ? (
                <LawFirmName name={lawFirm.companyName} isPrivate={lawFirm.isPrivate} />
            ) : (
                ''
            );
        }
        return allOptionLabel;
    };

    return (
        <FormControl
            sx={{
                backgroundColor: theme.palette.neutral.N_50,
                height: '40px',
                boxSizing: 'border-box',
                borderRadius: '8px',
                color: theme.palette.neutral.gray,
                minWidth: '200px',
            }}
            size="small"
        >
            <InputLabel id={labelId}>Select Law Firm</InputLabel>
            <Select
                labelId={labelId}
                value={selectValue}
                label="Select Law Firm"
                onChange={handleChange}
                renderValue={renderValue}
                MenuProps={{ autoFocus: false }}
                onClose={() => setSearchTerm('')}
            >
                <ListSubheader>
                    <TextField
                        placeholder="Search..."
                        value={searchTerm}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key !== 'Escape') {
                                event.stopPropagation();
                            }
                        }}
                        fullWidth
                        autoFocus
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </ListSubheader>
                {myLawFirmId !== undefined && myLawFirmName ? (
                    <MenuItem key={myLawFirmId} value={myLawFirmId}>
                        {myLawFirmName}
                    </MenuItem>
                ) : (
                    <MenuItem key="All" value="">
                        {allOptionLabel}
                    </MenuItem>
                )}
                {filteredLawFirms.map((lawFirm) => (
                    <MenuItem key={lawFirm.lawFirmId} value={lawFirm.lawFirmId}>
                        <LawFirmName name={lawFirm.companyName} isPrivate={lawFirm.isPrivate} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
